import { useTranslation } from "react-i18next";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../components/SecondaryButton/SecondaryButton";
import PageWrapper from "../PageWrapper/PageWrapper";
import Card from "../../components/Card/Card";
import { PermissionFlags } from "../../components/PermissionFlags/PermissionFlags";
import SlideOver from "../../components/SlideOver/SlideOver";
import { useEffect, useState } from "react";
import BookingForm from "../../components/BookingForm/BookingForm";
import BookingTypeCard from "../../components/BookingTypeCard/BookingTypeCard";
import ListContainer from "../../components/ListContainer/ListContainer";
import ListElement from "../../components/ListElement/ListElement";
import ElementActions from "../../components/ElementActions/ElementActions";
import IconButton from "../../components/IconButton/IconButton";
import ConfirmationPopup from "../../components/ConfirmationPopup/ConfirmationPopup";
import { Icon } from "../../components/Icon/Icon";
import deleteIcon from "../../assets/images/bin.svg";
import edit from "../../assets/images/pen.svg";
import { useBookingPlans } from '../../hooks/useBookingPlans';
import { BookingPlan } from '../../api/BookingPlansAPI';
import NoDataCTA from "../../components/NoDataCTA/NoDataCTA";
import Switch from "../../components/Switch/Switch";
import { Settings } from "../../store/reducers/AccountSlice";
import { useSelector } from "react-redux";
import CopyText from '../../components/CopyText/CopyText';
import { ChevronDown, ChevronUp, Globe, Instagram, Menu } from "lucide-react";
import { ButtonV2 } from "../../components/ButtonV2/ButtonV2";
import { Link } from "react-router-dom";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../components/Collapsible/Collapsible";
import { AdminRestricted } from "../../components/AdminRestricted/AdminRestricted";
import { MeetingType } from "../../shared/meetingTypeToTranslationKey";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/Dialog/Dialog";
import { InputV2 } from "../../components/InputV2/InputV2";
import { InstallInstructionsModal } from "./InstallInstructionsModal/InstallInstructionsModal";
import { useAliasValidation } from '../../hooks/useAliasValidation';

const EMPTY_BOOKING: BookingPlan = {
  name: "",
  internalType: 'VISIT' as MeetingType,
  customerInfo: "",
  automaticallyAccept: false,
  requiresFee: false,
  duration: "60",
  useCalendarAvailability: true,
  enabled: true,
  weekdayHours: {
    Monday: { enabled: true, opening: "09:00", closing: "17:00" },
    Tuesday: { enabled: true, opening: "09:00", closing: "17:00" },
    Wednesday: { enabled: true, opening: "09:00", closing: "17:00" },
    Thursday: { enabled: true, opening: "09:00", closing: "17:00" },
    Friday: { enabled: true, opening: "09:00", closing: "17:00" },
    Saturday: { enabled: true, opening: "10:00", closing: "16:00" },
    Sunday: { enabled: false, opening: "", closing: "" },
  },
  customerData: {
    requestEmail: true,
    requestPhone: true,
    requestName: true,
    requestBudget: false,
    requestSize: false,
    requestWeddingDate: false,
    requestNumberOfPeople: false,
    allowSpecialComments: false,
    requestWhereHeardFrom: false,
  },
  budgetRanges: [],
  brideCanCancel: false,
  requiresRefundableFee: false,
  useAdditionalRestrictions: false,
  useOnlinePayments: false,
};

const BookingSettings = () => {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation();

  return (
    <div className="w-full mt-4 p-4 bg-white rounded-lg shadow-sm">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <ButtonV2 variant="ghost" className="w-full justify-between">
            {t("bookings.settings.cta")}
            {isOpen ? (
              <ChevronUp className="h-4 w-4 ml-2" />
            ) : (
              <ChevronDown className="h-4 w-4 ml-2" />
            )}
          </ButtonV2>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2 space-y-2">
          <p className="text-sm text-gray-600">
            {t("bookings.settings.description")}
          </p>
          <ButtonV2 asChild size="sm">
            <Link to="/settings">{t("bookings.settings.button")}</Link>
          </ButtonV2>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

const BookingLinkSection = ({ bookingLink, customLinkText, onCustomLinkTextChange }: { 
  bookingLink: string, 
  customLinkText: string;
  onCustomLinkTextChange: (text: string) => void; }) => {

  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [tempCustomLinkText, setTempCustomLinkText] = useState(customLinkText);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  
  const { validateAliasFormat, checkAliasAvailability, debouncedCheckAvailability, isChecking, isValidationInProgress, currentAbortController } = useAliasValidation();

  const validateCustomLinkText = (text: string): boolean => {
    const validation = validateAliasFormat(text);
    setError(validation.error);
    setAvailabilityError(null);
    return validation.isValid;
  };

  const handleCustomLinkTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value.toLowerCase();
    setTempCustomLinkText(newText);
    setHasChanges(newText !== customLinkText);
    
    if (newText === customLinkText) {
      setError(null);
      setAvailabilityError(null);
      setHasChanges(false);
    } else {
      const isValid = validateCustomLinkText(newText);
      
      if (isValid && newText.length >= 3) {
        debouncedCheckAvailability(
          newText,
          (alias: string) => import('../../api/BookingPlansAPI').then(api => api.default.checkAliasAvailability(alias, currentAbortController?.signal)),
          (result) => {
            if (!result.isAvailable) {
              setAvailabilityError(result.error || 'Alias is not available');
            } else {
              setAvailabilityError(null);
            }
          }
        );
      }
    }
  };

  const handleSaveCustomLink = async () => {
    if (!validateCustomLinkText(tempCustomLinkText)) return;
    
    const availabilityCheck = await checkAliasAvailability(
      tempCustomLinkText, 
      (alias: string) => import('../../api/BookingPlansAPI').then(api => api.default.checkAliasAvailability(alias))
    );
    
    if (!availabilityCheck.isAvailable) {
      setAvailabilityError(availabilityCheck.error || 'Alias is not available');
      return;
    }
    
    onCustomLinkTextChange(tempCustomLinkText);
    setIsEditModalOpen(false);
    setHasChanges(false);
    setError(null);
    setAvailabilityError(null);
  };

  const handleCancel = () => {
    setTempCustomLinkText(customLinkText);
    setError(null);
    setAvailabilityError(null);
    setHasChanges(false);
    setIsEditModalOpen(false);
  };

  const renderBookingLink = () => {
    const parts = bookingLink.split('/');
    const customPart = parts.pop();
    const basePart = parts.join('/');
  
    return (
      <div className="flex flex-wrap items-center gap-2 bg-gray-100 p-2 rounded-md overflow-hidden">
        <span className="text-gray-500 truncate w-full sm:w-auto">
          {basePart}/
        </span>
        <span className="font-medium truncate w-full sm:w-auto">
          {customPart}
        </span>
        <div className="shrink-0">
          <CopyText text={bookingLink} />
        </div>
      </div>
    );
  };

  return (
    <div className="mt-6 bg-gray-50 rounded-lg p-4 w-full overflow-x-auto">
      <div className="flex flex-wrap items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{t("bookings.widget.copy")}</h3>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-primary-600 hover:text-primary-700 focus:outline-none"
        >
          {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
        </button>
      </div>
      {renderBookingLink()}
      <div className="mt-4 sm:space-x-1">
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogTrigger asChild>
            <ButtonV2 variant="outline" size="sm" className="text-xs sm:text-sm">
              {t("bookings.widget.customize_link")}
            </ButtonV2>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("bookings.widget.customize_link_title")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="relative">
                <InputV2
                  value={tempCustomLinkText}
                  onChange={handleCustomLinkTextChange}
                  placeholder={t("bookings.widget.custom_link_placeholder")!}
                />
                {isValidationInProgress && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="validation-spinner"></div>
                  </div>
                )}
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              {availabilityError && <p className="text-red-500 text-sm">{availabilityError}</p>}
              <div className="flex justify-center space-x-3">
                <SecondaryButton text={t("global.cancel")!} fn={handleCancel} />
                <PrimaryButton 
                  text={t("global.save")!} 
                  fn={handleSaveCustomLink} 
                  isDisabled={!!error || isChecking || isValidationInProgress || !hasChanges || !!availabilityError}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
        <InstallInstructionsModal />
      </div>
      {isExpanded && 
        <div className="mt-4 space-y-3">
          <p className="text-sm text-gray-600">{t("bookings.widget.instructions")}</p>
          <ul className="space-y-2">
            <li className="flex items-center space-x-2 text-sm">
              <Globe className="h-4 w-4 text-primary-600" />
              <span>{t("bookings.widget.website")}</span>
            </li>
            <li className="flex items-center space-x-2 text-sm">
              <Instagram className="h-4 w-4 text-primary-600" />
              <span>{t("bookings.widget.instagram")}</span>
            </li>
            <li className="flex items-center space-x-2 text-sm">
              <Menu className="h-4 w-4 text-primary-600" />
              <span>{t("bookings.widget.menu")}</span>
            </li>
          </ul>
        </div>
      }
    </div>
  );
};

const BookingsPage = () => {
  const { t } = useTranslation();
  const [editOpen, setEditOpen] = useState<boolean>(false);
  const [formData, setFormData] = useState<BookingPlan>(EMPTY_BOOKING);
  const [isOperationLoading, setIsOperationLoading] = useState<boolean>(false);
  const [customLinkText, setCustomLinkText] = useState<string>("");
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const settings: Settings = useSelector((state: any) => state.settings.value);

  const {
    bookingPlans,
    isLoading,
    createBookingPlan,
    updateBookingPlan,
    deleteBookingPlan,
    setBookingLink
  } = useBookingPlans();

  const handleEdit = (booking: BookingPlan) => {
    setFormData(booking);
    setEditOpen(true);
  };

  const handleDelete = async (booking: BookingPlan) => {
    setIsOperationLoading(true);
    await deleteBookingPlan(booking.id!);
    setIsOperationLoading(false);
  };

  const handleQuickSave = async (booking: BookingPlan) => {
    await updateBookingPlan(booking.id!, { ...booking, enabled: !booking.enabled })
  };

  const bookingLink = `${window.location.origin}/bookings/${customLinkText || settings.bookingWidgetId}`;

  useEffect(() => {
    setCustomLinkText(settings.bookingWidgetId || "");
  }, [settings.bookingWidgetId]);

  const handleCustomLinkTextChange = (text: string) => {
    setCustomLinkText(text);
    setBookingLink(text);
  };

  const handleSave = async () => {
    setIsOperationLoading(true);
    if (formData.id) {
      await updateBookingPlan(formData.id, formData);
    } else {
      await createBookingPlan(formData);
    }
    setIsOperationLoading(false);
    setEditOpen(false);
    setFormData(EMPTY_BOOKING);
    setFormErrors({});
  };

  const handleNewBooking = () => {
    setFormData(EMPTY_BOOKING);
    setEditOpen(true);
  }

  return (
    <PageWrapper>
      <>
        <div className="space-y-5">
          <Card>
            <div className="flex flex-wrap">
              <div className="container flex flex-wrap space-x-5">
                <PermissionFlags requiredFlags={['edit-customers'] || []}>
                  <PrimaryButton text={t("bookings.empty.cta")!} fn={handleNewBooking} />
                </PermissionFlags>
              </div>
              <BookingLinkSection 
                bookingLink={bookingLink} 
                customLinkText={customLinkText}
                onCustomLinkTextChange={handleCustomLinkTextChange}
              />
            </div>
          </Card>
          <Card>
            <>
              <div className="overflow-x-scroll w-full">
                <ListContainer
                  isLoading={isLoading}
                  elements={bookingPlans}
                  emptyListComponent={() => 
                    <NoDataCTA
                      headline={t("bookings.empty.title")!}
                      subtext={t("bookings.empty.body")!}
                      primaryActionText={t("bookings.empty.cta")}
                      onPrimaryAction={handleNewBooking}
                    /> }
                  toElementFn={(booking: BookingPlan) => (
                    <ListElement
                      key={booking.id}
                      css="bg-white text-black hover:bg-darker-gold-100"
                    >
                      <ElementActions
                        actions={
                          <div className="flex space-x-3 items-center">
                            <Switch
                              id={`${booking.name}-${booking.enabled}`}
                              checked={booking.enabled}
                              onChange={() => handleQuickSave(booking)}
                            />
                            <IconButton onClick={() => handleEdit(booking)}>
                              <Icon url={edit} cssStyles="w-5"/>
                            </IconButton>
                            <PermissionFlags requiredFlags={['remove-meetings'] || []}>
                              <ConfirmationPopup onConfirm={() => handleDelete(booking)} confirmText={t("global.confirm")} title={`${t("global.remove")}: ${booking.name}`}>
                                <Icon url={deleteIcon} cssStyles="w-5" />
                              </ConfirmationPopup>
                            </PermissionFlags>
                          </div>
                        }
                      >
                        <BookingTypeCard booking={booking} onEdit={() => handleEdit(booking)}/>
                      </ElementActions>
                    </ListElement>
                  )}
                />
              </div>
              <AdminRestricted >
                <BookingSettings />
              </AdminRestricted>
            </>
          </Card>
        </div>
        <SlideOver 
          isOpen={editOpen}
          setIsOpen={setEditOpen}
          title={formData.name || t("bookings.form.title")!}
          primaryButton={{
            label: t("global.save"),
            onClick: handleSave
          }}
          secondaryButton={{
            label: t("global.cancel"),
            onClick: () => setEditOpen(false)
          }}
          isLoading={isOperationLoading}
          hasErrors={Object.keys(formErrors).length > 0}
        >
          <BookingForm booking={formData} onChange={(updatedBooking, errors) => {setFormData(updatedBooking);setFormErrors(errors);}}  />
        </SlideOver>
      </>
    </PageWrapper>
  );
};

export default BookingsPage;